import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { apiUrl } from '../services/api';
import './AddProperty.css';

const PROPERTY_TYPES = [
  { value: 'single_family', label: 'Single Family' },
  { value: 'multi_family', label: 'Multi Family' },
  { value: 'condo', label: 'Condo' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'commercial', label: 'Commercial' },
];

const emptyForm = {
  nickname: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip: '',
  property_type: 'single_family',
  bedrooms: '',
  bathrooms: '',
  square_feet: '',
  year_built: '',
  purchase_price: '',
  asking_price: '',
  purchase_date: '',
  current_value: '',
  notes: '',
  // Loan fields
  lender_name: '',
  original_principal: '',
  interest_rate: '',
  loan_term_months: '',
  loan_start_date: '',
  monthly_payment: '',
};

const AddProperty = () => {
  const [form, setForm] = useState(emptyForm);
  const [hasLoan, setHasLoan] = useState(true);
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const navigate = useNavigate();
  const toast = useRef(null);
  const fileInputRef = useRef(null);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (!hasLoan) {
        delete payload.lender_name;
        delete payload.original_principal;
        delete payload.interest_rate;
        delete payload.loan_term_months;
        delete payload.loan_start_date;
        delete payload.monthly_payment;
      }
      const res = await fetch(apiUrl('/properties'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify(payload),
      });
      if (res.status === 401) { navigate('/login', { replace: true }); return; }
      const data = await res.json();
      if (!res.ok) {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: data.error || 'Failed to save property', life: 4000 });
        return;
      }

      // Upload photo if provided
      if (photoFile && data.id) {
        const fd = new FormData();
        fd.append('photo', photoFile);
        fd.append('is_primary', 'true');
        await fetch(apiUrl(`/properties/${data.id}/photos`), {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
          body: fd,
        });
      }

      toast.current?.show({ severity: 'success', summary: 'Property Added', detail: 'Redirecting to properties list...', life: 2000 });
      setTimeout(() => navigate('/properties'), 2000);
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Connection Error', detail: 'Unable to connect to server', life: 4000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ap-page">
      <Toast ref={toast} position="top-right" />
      <div className="ap-header">
        <h1>Add Property</h1>
        <p>Enter all the details about the property and its loan.</p>
      </div>

      <form className="ap-form" onSubmit={handleSubmit}>

        {/* Property Details */}
        <section className="ap-section">
          <h2 className="ap-section-title">Property Details</h2>

          <div className="ap-row">
            <div className="ap-field">
              <label>Nickname <span className="ap-optional">(optional)</span></label>
              <input type="text" value={form.nickname} onChange={set('nickname')} placeholder="e.g. Lake House" />
            </div>
            <div className="ap-field">
              <label>Property Type</label>
              <select value={form.property_type} onChange={set('property_type')} required>
                {PROPERTY_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="ap-field ap-field--full">
            <label>Street Address</label>
            <input type="text" value={form.address_line1} onChange={set('address_line1')} placeholder="123 Main St" required />
          </div>

          <div className="ap-field ap-field--full">
            <label>Address Line 2 <span className="ap-optional">(optional)</span></label>
            <input type="text" value={form.address_line2} onChange={set('address_line2')} placeholder="Apt, Suite, Unit..." />
          </div>

          <div className="ap-row ap-row--4">
            <div className="ap-field">
              <label>City</label>
              <input type="text" value={form.city} onChange={set('city')} required />
            </div>
            <div className="ap-field">
              <label>State</label>
              <input type="text" value={form.state} onChange={set('state')} placeholder="TX" maxLength={2} required />
            </div>
            <div className="ap-field">
              <label>ZIP</label>
              <input type="text" value={form.zip} onChange={set('zip')} placeholder="78701" required />
            </div>
          </div>

          <div className="ap-row ap-row--4">
            <div className="ap-field">
              <label>Bedrooms</label>
              <input type="number" value={form.bedrooms} onChange={set('bedrooms')} min={0} step={1} />
            </div>
            <div className="ap-field">
              <label>Bathrooms</label>
              <input type="number" value={form.bathrooms} onChange={set('bathrooms')} min={0} step={0.5} />
            </div>
            <div className="ap-field">
              <label>Sq Feet</label>
              <input type="number" value={form.square_feet} onChange={set('square_feet')} min={0} />
            </div>
            <div className="ap-field">
              <label>Year Built</label>
              <input type="number" value={form.year_built} onChange={set('year_built')} min={1800} max={2100} />
            </div>
          </div>
        </section>

        {/* Purchase Info */}
        <section className="ap-section">
          <h2 className="ap-section-title">Purchase Information</h2>
          <div className="ap-row ap-row--3">
            <div className="ap-field">
              <label>Purchase Price</label>
              <div className="ap-input-prefix">
                <span>$</span>
                <input type="number" value={form.purchase_price} onChange={set('purchase_price')} min={0} step={0.01} required />
              </div>
            </div>
            <div className="ap-field">
              <label>Asking Price <span className="ap-optional">(defaults to purchase)</span></label>
              <div className="ap-input-prefix">
                <span>$</span>
                <input type="number" value={form.asking_price} onChange={set('asking_price')} min={0} step={0.01} />
              </div>
            </div>
            <div className="ap-field">
              <label>Purchase Date</label>
              <input type="date" value={form.purchase_date} onChange={set('purchase_date')} required />
            </div>
            <div className="ap-field">
              <label>Current Value <span className="ap-optional">(optional)</span></label>
              <div className="ap-input-prefix">
                <span>$</span>
                <input type="number" value={form.current_value} onChange={set('current_value')} min={0} step={0.01} />
              </div>
            </div>
          </div>
        </section>

        {/* Loan Info */}
        <section className="ap-section">
          <div className="ap-section-title-row">
            <h2 className="ap-section-title">Loan Information</h2>
            <label className="ap-toggle">
              <input type="checkbox" checked={hasLoan} onChange={(e) => setHasLoan(e.target.checked)} />
              <span>Has a mortgage</span>
            </label>
          </div>

          {hasLoan && (
            <>
              <div className="ap-row">
                <div className="ap-field">
                  <label>Lender Name</label>
                  <input type="text" value={form.lender_name} onChange={set('lender_name')} placeholder="e.g. Wells Fargo" />
                </div>
                <div className="ap-field">
                  <label>Loan Start Date</label>
                  <input type="date" value={form.loan_start_date} onChange={set('loan_start_date')} />
                </div>
              </div>
              <div className="ap-row ap-row--4">
                <div className="ap-field">
                  <label>Original Principal</label>
                  <div className="ap-input-prefix">
                    <span>$</span>
                    <input type="number" value={form.original_principal} onChange={set('original_principal')} min={0} step={0.01} />
                  </div>
                </div>
                <div className="ap-field">
                  <label>Interest Rate %</label>
                  <input type="number" value={form.interest_rate} onChange={set('interest_rate')} min={0} max={30} step={0.001} placeholder="6.750" />
                </div>
                <div className="ap-field">
                  <label>Term (months)</label>
                  <input type="number" value={form.loan_term_months} onChange={set('loan_term_months')} min={1} placeholder="360" />
                </div>
                <div className="ap-field">
                  <label>Monthly Payment</label>
                  <div className="ap-input-prefix">
                    <span>$</span>
                    <input type="number" value={form.monthly_payment} onChange={set('monthly_payment')} min={0} step={0.01} />
                  </div>
                </div>
              </div>
            </>
          )}
        </section>

        {/* Photo */}
        <section className="ap-section">
          <h2 className="ap-section-title">Property Photo <span className="ap-optional">(optional)</span></h2>
          <div className="ap-photo-upload" onClick={() => fileInputRef.current.click()}>
            {photoPreview ? (
              <img src={photoPreview} alt="Preview" className="ap-photo-preview" />
            ) : (
              <div className="ap-photo-placeholder">
                <i className="pi pi-image" />
                <span>Click to upload a photo</span>
                <span className="ap-optional">JPG, PNG, WEBP up to 10MB</span>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handlePhotoChange}
          />
          {photoPreview && (
            <button type="button" className="ap-btn-cancel" style={{ marginTop: '0.5rem', alignSelf: 'flex-start' }}
              onClick={() => { setPhotoFile(null); setPhotoPreview(null); fileInputRef.current.value = ''; }}>
              Remove photo
            </button>
          )}
        </section>

        {/* Notes */}
        <section className="ap-section">
          <h2 className="ap-section-title">Notes <span className="ap-optional">(optional)</span></h2>
          <textarea className="ap-textarea" value={form.notes} onChange={set('notes')} rows={4} placeholder="Any additional notes about this property..." />
        </section>

        <div className="ap-actions">
          <button type="button" className="ap-btn-cancel" onClick={() => navigate('/properties')}>Cancel</button>
          <button type="submit" className="ap-btn-submit" disabled={loading}>
            {loading ? 'Saving...' : 'Save Property'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddProperty;
